import { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, RotateCcw, Save, Sun, Moon, Monitor, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { useTheme } from "@/components/ThemeProvider";
import { AtmosphereLayer } from "@/components/appearance/AtmosphereLayer";
import { MotifLibrary } from "@/components/appearance/MotifLibrary";
import {
  GLOW_STYLES, GLOW_INTENSITIES, ATMOSPHERE_SURFACES, PLACEMENT_SLOTS, MOTIF_PACKS,
} from "@/lib/appearance";

const POSITIONS = ["top-right", "top-left", "bottom-right", "bottom-left", "center", "top"];
const VIS = [{ v: "full", l: "Full" }, { v: "reduced", l: "Reduced" }, { v: "off", l: "Off" }];
const PACK_LABEL = { shiva: "Shiva", lalita: "Lalita", vishnu: "Vishnu", universal: "Universal", devotional: "Devotional", custom: "Custom" };

// Preview contexts (surface + friendly label). Dense defaults to minimal.
const PREVIEW_CTX = [
  { surface: "login", label: "Login", tall: true },
  { surface: "dashboard_hero", label: "Home / Dashboard" },
  { surface: "workspace_header", label: "Workspace" },
  { surface: "empty_state", label: "Empty state" },
  { surface: "dense", label: "Dense screen" },
];

export function AtmospherePanel({ isAdmin }) {
  const { atmosphere, atmospherePersonal, setAtmospherePersonal, refreshAtmosphere, motifs } = useTheme();
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState(null);
  const [draft, setDraft] = useState(null);     // working config {glow, surfaces, slots}
  const [dirty, setDirty] = useState(false);
  const [pvMode, setPvMode] = useState("light");
  const [pvMobile, setPvMobile] = useState(false);
  const [packs, setPacks] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ps, a, pk] = await Promise.all([
        apiFetch("/atmosphere-presets"), apiFetch("/appearance"), apiFetch("/motif-packs"),
      ]);
      setPresets(ps); setPacks(pk);
      const atm = a.atmosphere || {};
      setPresetId(atm.preset_id || null);
      setDraft({ glow: atm.glow || {}, surfaces: atm.surfaces || {}, slots: atm.slots || {} });
      setDirty(false);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeMotifs = useMemo(() => (motifs || []).filter((m) => m.status !== "archived"), [motifs]);

  const setGlow = (k, v) => { setDraft((d) => ({ ...d, glow: { ...d.glow, [k]: v } })); setDirty(true); };
  const setGlowMode = (mode, k, v) => { setDraft((d) => ({ ...d, glow: { ...d.glow, [mode]: { ...(d.glow[mode] || {}), [k]: v } } })); setDirty(true); };
  const setSurface = (s, v) => { setDraft((d) => ({ ...d, surfaces: { ...d.surfaces, [s]: v } })); setDirty(true); };
  const setSlot = (id, k, v) => { setDraft((d) => ({ ...d, slots: { ...d.slots, [id]: { ...(d.slots[id] || {}), [k]: v } } })); setDirty(true); };

  const applyPreset = (id) => {
    const p = presets.find((x) => x.id === id); if (!p) return;
    setPresetId(id);
    setDraft({ glow: p.config.glow || {}, surfaces: p.config.surfaces || {}, slots: p.config.slots || {} });
    setDirty(true);
  };

  const save = async () => {
    try {
      await apiFetch("/appearance/atmosphere", { method: "PUT", body: JSON.stringify({
        atmosphere_preset_id: presetId, atmosphere_override: draft }) });
      await refreshAtmosphere();
      setDirty(false);
      toast.success("Atmosphere applied for the organisation");
    } catch (e) { toast.error(e.message); }
  };
  const reset = async () => {
    try {
      await apiFetch("/appearance/atmosphere", { method: "PUT", body: JSON.stringify({ reset: true }) });
      await refreshAtmosphere(); await load();
      toast.success("Reset to Nirvana Default");
    } catch (e) { toast.error(e.message); }
  };

  // ----- personal-only control (all users) -----
  const PersonalCard = (
    <Card className="mb-6 flex items-center justify-between p-5 shadow-soft" data-testid="atmosphere-personal">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><Sparkles className="h-5 w-5" /></span>
        <div>
          <h3 className="font-heading text-lg font-semibold">Atmosphere for me</h3>
          <p className="text-xs text-muted-foreground">Turn decorative glow &amp; motifs off for a focused, faster view. This only affects your account.</p>
        </div>
      </div>
      <Select value={atmospherePersonal || "follow"} onValueChange={setAtmospherePersonal}>
        <SelectTrigger className="w-44" data-testid="atmosphere-personal-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="follow">Follow organisation</SelectItem>
          <SelectItem value="off">Off (focus mode)</SelectItem>
        </SelectContent>
      </Select>
    </Card>
  );

  if (!draft) return <div data-testid="atmosphere-panel">{PersonalCard}</div>;

  if (!isAdmin) {
    return (
      <div data-testid="atmosphere-panel">
        {PersonalCard}
        <Card className="p-5 shadow-soft">
          <p className="text-sm text-muted-foreground">The organisation Atmosphere is configured by administrators. You can turn it off for yourself above.</p>
        </Card>
      </div>
    );
  }

  const glow = draft.glow || {};

  return (
    <div data-testid="atmosphere-panel">
      {PersonalCard}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* ---- controls ---- */}
        <div className="space-y-6">
          {/* preset selector */}
          <Card className="p-5 shadow-soft">
            <h3 className="mb-1 font-heading text-lg font-semibold">Atmosphere preset</h3>
            <p className="mb-3 text-xs text-muted-foreground">A starting point for glow &amp; placement. Fine-tune below, then Save.</p>
            <div className="space-y-2">
              {presets.map((p) => (
                <button key={p.id} onClick={() => applyPreset(p.id)} data-testid={`atmosphere-preset-${p.id}`}
                  aria-pressed={presetId === p.id}
                  className={cn("flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
                    presetId === p.id ? "border-accent bg-accent-subtle" : "border-border hover:bg-muted/50")}>
                  <div>
                    <div className="text-sm font-medium">{p.name} {p.is_preset && <span className="ml-1 text-[10px] uppercase text-muted-foreground">Built-in</span>}</div>
                    <div className="text-xs text-muted-foreground capitalize">{p.config?.glow?.style?.replace("_", " ")} · {p.config?.glow?.intensity}</div>
                  </div>
                  {presetId === p.id && <Check className="h-4 w-4 text-accent" />}
                </button>
              ))}
            </div>
          </Card>

          {/* ambient glow */}
          <Card className="p-5 shadow-soft" data-testid="glow-controls">
            <h3 className="mb-3 font-heading text-lg font-semibold">Ambient glow</h3>
            <Label className="text-xs">Style</Label>
            <div className="mb-4 mt-1 grid grid-cols-2 gap-2">
              {GLOW_STYLES.map((s) => (
                <button key={s.value} onClick={() => setGlow("style", s.value)} data-testid={`glow-style-${s.value}`}
                  className={cn("rounded-lg border p-2.5 text-left transition-colors",
                    glow.style === s.value ? "border-accent bg-accent-subtle" : "border-border hover:bg-muted/50")}>
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="text-[11px] text-muted-foreground">{s.desc}</div>
                </button>
              ))}
            </div>
            <div className="mb-4">
              <Label className="text-xs">Intensity</Label>
              <Select value={glow.intensity || "subtle"} onValueChange={(v) => setGlow("intensity", v)}>
                <SelectTrigger className="mt-1" data-testid="glow-intensity"><SelectValue /></SelectTrigger>
                <SelectContent>{GLOW_INTENSITIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mb-4">
              <Label className="text-xs">Palette</Label>
              <Select value={glow.palette || "auto"} onValueChange={(v) => setGlow("palette", v)}>
                <SelectTrigger className="mt-1" data-testid="glow-palette"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Follow theme (recommended)</SelectItem>
                  <SelectItem value="custom">Custom colours</SelectItem>
                </SelectContent>
              </Select>
              {glow.palette === "custom" && (
                <div className="mt-2 flex gap-3">
                  <label className="flex items-center gap-2 text-xs">Colour A
                    <input type="color" value={glow.custom?.a || "#c5a059"} onChange={(e) => setGlow("custom", { ...glow.custom, a: e.target.value })} className="h-8 w-10 rounded border" data-testid="glow-custom-a" /></label>
                  <label className="flex items-center gap-2 text-xs">Colour B
                    <input type="color" value={glow.custom?.b || "#1c3d2d"} onChange={(e) => setGlow("custom", { ...glow.custom, b: e.target.value })} className="h-8 w-10 rounded border" data-testid="glow-custom-b" /></label>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["light", "dark"].map((m) => (
                <div key={m}>
                  <div className="mb-1 flex items-center justify-between">
                    <Label className="text-xs capitalize">{m} visibility</Label>
                    <Switch checked={(glow[m]?.enabled ?? true)} onCheckedChange={(v) => setGlowMode(m, "enabled", v)} data-testid={`glow-${m}-enabled`} />
                  </div>
                  <Slider min={0} max={2} step={0.1} value={[glow[m]?.opacity ?? 1]} disabled={glow[m]?.enabled === false}
                    onValueChange={([v]) => setGlowMode(m, "opacity", v)} data-testid={`glow-${m}-opacity`} />
                </div>
              ))}
            </div>
          </Card>

          {/* surface visibility */}
          <Card className="p-5 shadow-soft" data-testid="surface-visibility">
            <h3 className="mb-1 font-heading text-lg font-semibold">Where glow appears</h3>
            <p className="mb-3 text-xs text-muted-foreground">Expressive at moments of orientation; clean during dense operational work.</p>
            <div className="space-y-2">
              {ATMOSPHERE_SURFACES.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{s.name}</span>
                  <div className="flex gap-1">
                    {VIS.map((v) => (
                      <button key={v.v} onClick={() => setSurface(s.id, v.v)} data-testid={`surface-${s.id}-${v.v}`}
                        className={cn("rounded-md border px-2 py-1 text-xs transition-colors",
                          (draft.surfaces[s.id] || "off") === v.v ? "border-accent bg-accent-subtle" : "border-border text-muted-foreground hover:bg-muted/50")}>
                        {v.l}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* motif placement */}
          <Card className="p-5 shadow-soft" data-testid="motif-placement">
            <h3 className="mb-1 font-heading text-lg font-semibold">Motif placement</h3>
            <p className="mb-3 text-xs text-muted-foreground">Place uploaded motifs into safe decorative slots. Dense screens never receive motifs.</p>
            {activeMotifs.length === 0 && (
              <p className="mb-3 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">Upload approved artwork in the Motif library below to enable placements.</p>
            )}
            <div className="space-y-2">
              {PLACEMENT_SLOTS.map((slot) => {
                const s = draft.slots[slot.id] || {};
                return (
                  <details key={slot.id} className="rounded-lg border border-border" data-testid={`slot-${slot.id}`}>
                    <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm">
                      <span>{slot.name}</span>
                      <Switch checked={!!s.enabled} onCheckedChange={(v) => setSlot(slot.id, "enabled", v)} data-testid={`slot-${slot.id}-enabled`} />
                    </summary>
                    {s.enabled && (
                      <div className="space-y-3 border-t border-border p-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Motif pack</Label>
                            <Select value={s.pack || "none"} onValueChange={(v) => setSlot(slot.id, "pack", v === "none" ? "" : v)}>
                              <SelectTrigger className="mt-1 h-8" data-testid={`slot-${slot.id}-pack`}><SelectValue placeholder="Any" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Any pack</SelectItem>
                                {MOTIF_PACKS.map((p) => <SelectItem key={p} value={p}>{PACK_LABEL[p]}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Specific motif</Label>
                            <Select value={s.motif_id || "auto"} onValueChange={(v) => setSlot(slot.id, "motif_id", v === "auto" ? "" : v)}>
                              <SelectTrigger className="mt-1 h-8" data-testid={`slot-${slot.id}-motif`}><SelectValue placeholder="Auto" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Auto (first in pack)</SelectItem>
                                {activeMotifs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Position</Label>
                            <Select value={s.position || "top-right"} onValueChange={(v) => setSlot(slot.id, "position", v)}>
                              <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Tint</Label>
                            <Select value={s.tint || "auto"} onValueChange={(v) => setSlot(slot.id, "tint", v)}>
                              <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Original</SelectItem>
                                <SelectItem value="light">Whiten (dark bg)</SelectItem>
                                <SelectItem value="dark">Darken (light bg)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div><div className="mb-1 flex justify-between text-xs"><span>Opacity</span><span>{Math.round((s.opacity ?? 0.06) * 100)}%</span></div>
                          <Slider min={0.01} max={0.3} step={0.01} value={[s.opacity ?? 0.06]} onValueChange={([v]) => setSlot(slot.id, "opacity", v)} data-testid={`slot-${slot.id}-opacity`} /></div>
                        <div><div className="mb-1 flex justify-between text-xs"><span>Scale</span><span>{(s.scale ?? 1).toFixed(1)}×</span></div>
                          <Slider min={0.4} max={2} step={0.1} value={[s.scale ?? 1]} onValueChange={([v]) => setSlot(slot.id, "scale", v)} /></div>
                        <div><div className="mb-1 flex justify-between text-xs"><span>Rotation</span><span>{s.rotation ?? 0}°</span></div>
                          <Slider min={-30} max={30} step={5} value={[s.rotation ?? 0]} onValueChange={([v]) => setSlot(slot.id, "rotation", v)} /></div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <label className="flex items-center justify-between rounded border border-border px-2 py-1">Light <Switch checked={s.light !== false} onCheckedChange={(v) => setSlot(slot.id, "light", v)} /></label>
                          <label className="flex items-center justify-between rounded border border-border px-2 py-1">Dark <Switch checked={s.dark !== false} onCheckedChange={(v) => setSlot(slot.id, "dark", v)} /></label>
                          <label className="flex items-center justify-between rounded border border-border px-2 py-1">Desktop <Switch checked={s.desktop !== false} onCheckedChange={(v) => setSlot(slot.id, "desktop", v)} /></label>
                          <label className="flex items-center justify-between rounded border border-border px-2 py-1">Mobile <Switch checked={!!s.mobile} onCheckedChange={(v) => setSlot(slot.id, "mobile", v)} /></label>
                        </div>
                        <div>
                          <Label className="text-xs">Variation</Label>
                          <Select value={s.variation || "fixed"} onValueChange={(v) => setSlot(slot.id, "variation", v)}>
                            <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed (stable)</SelectItem>
                              <SelectItem value="gentle">Gentle variation</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </details>
                );
              })}
            </div>
          </Card>
        </div>

        {/* ---- live preview ---- */}
        <div className="space-y-4">
          <Card className="p-5 shadow-soft" data-testid="atmosphere-preview">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-medium">Live preview</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPvMode("light")} className={cn("rounded-md border p-1.5", pvMode === "light" ? "border-accent bg-accent-subtle" : "border-border")} data-testid="preview-light"><Sun className="h-4 w-4" /></button>
                <button onClick={() => setPvMode("dark")} className={cn("rounded-md border p-1.5", pvMode === "dark" ? "border-accent bg-accent-subtle" : "border-border")} data-testid="preview-dark"><Moon className="h-4 w-4" /></button>
                <span className="mx-1 h-4 w-px bg-border" />
                <button onClick={() => setPvMobile(false)} className={cn("rounded-md border p-1.5", !pvMobile ? "border-accent bg-accent-subtle" : "border-border")} data-testid="preview-desktop"><Monitor className="h-4 w-4" /></button>
                <button onClick={() => setPvMobile(true)} className={cn("rounded-md border p-1.5", pvMobile ? "border-accent bg-accent-subtle" : "border-border")} data-testid="preview-mobile"><Smartphone className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">Preview is temporary — nothing changes for the organisation until you Save.</p>
            <div className={cn("grid gap-3", pvMobile ? "grid-cols-1" : "grid-cols-2")}>
              {PREVIEW_CTX.map((ctx) => (
                <div key={ctx.surface} className={cn(pvMode === "dark" ? "dark" : "", ctx.tall && !pvMobile ? "col-span-2" : "")}>
                  <div className="mb-1 text-[11px] font-medium text-muted-foreground">{ctx.label}</div>
                  <div className="relative h-28 overflow-hidden rounded-lg border border-border"
                    style={{ background: pvMode === "dark" ? "rgb(15 26 20)" : "rgb(250 248 245)" }}>
                    <AtmosphereLayer surface={ctx.surface} slot={ctx.surface}
                      overrideAtmosphere={draft} overrideMode={pvMode} overrideMotifs={activeMotifs} overrideMobile={pvMobile} />
                    <div className="relative z-10 flex h-full items-center justify-center px-3 text-center">
                      <span className={cn("font-heading text-sm", pvMode === "dark" ? "text-[#eaf0ec]" : "text-[#1a2e23]")}>
                        {ctx.surface === "dense" ? "Table · Form · Editor" : "Sample content"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={reset} data-testid="atmosphere-reset"><RotateCcw className="h-4 w-4" /> Reset to default</Button>
            <Button className="gap-1.5" disabled={!dirty} onClick={save} data-testid="atmosphere-save"><Save className="h-4 w-4" /> {dirty ? "Save & apply" : "Saved"}</Button>
          </div>
        </div>
      </div>

      <div className="mt-6"><MotifLibrary onChanged={refreshAtmosphere} /></div>
    </div>
  );
}
